import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import { downloadOrderSummary, getOrderDetailsByNumber } from "../../../api/orders";
import { getOrderImageUrl } from "../../../api/orderImages";
import { downloadReportCSV } from '../../lib/functions';
import Button from '../Button';
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const DispatchReport = ({ dispatchData = [], theme }) => {
    const { profile, isAdmin, isCustomer } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);
    const [summaryStats, setSummaryStats] = useState({});
    
    // Order Details Modal State
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    const [isDownloadingSummary, setIsDownloadingSummary] = useState(false);

    // Image Modal State
    const [showImageModal, setShowImageModal] = useState(false);
    const [orderImageUrl, setOrderImageUrl] = useState(null);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'customer_company', label: 'Customer' },
        { key: 'order_number', label: 'Order Number' },
        { key: 'delivery_company', label: 'Delivery Company' },
        { key: 'product_names', label: 'Products' },
        { key: 'total_pallet_count', label: 'Pallet Count' },
        { key: 'notes', label: 'Notes' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);
    const [allowDownload, setAllowDownload] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true); // Open password modal on component mount
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
                setAllowDownload(true); // Allow download if password is valid
            } else {
                logger.log("Invalid password.");
                dispatchData.length = 0; // Clear dispatchData if password is invalid
                showAlert({
                    message: 'Password verification failed.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error verifying password:", error);
            showAlert({
                message: 'An error occurred during password verification.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
        setOpenRequestPassword(false); // Close the modal
    };
    
    const showData = () => {
        if (dispatchData && dispatchData.length > 0) {
            // Format the data for display
            const formatted = dispatchData.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'order_number') {
                        newEntry[key] = entry[key] ? entry[key] : 'Manual';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(formatted);
        }
    };

    const calculateSummaryStats = (data) => {
        if (!data || data.length === 0) {
            setSummaryStats({});
            return;
        }

        const stats = {
            totalOrders: data.length,
            totalPallets: data.reduce((sum, item) => sum + (parseInt(item.total_pallet_count) || 0), 0),
            uniqueCustomers: [...new Set(data.map(item => item.customer_company))].length,
            totalEntries: data.reduce((sum, item) => sum + (parseInt(item.entry_count) || 0), 0),
            manualOrders: data.filter(item => item.order_number === 'Manual').length,
            systemOrders: data.filter(item => item.order_number !== 'Manual' && item.order_number && item.order_number !== '—').length,
        };

        setSummaryStats(stats);
    };

    // Calculate filtered totals for table footer
    const calculateFilteredTotals = () => {
        if (!tableData || tableData.length === 0) {
            return {
                totalPallets: 0,
                totalQuantity: 0,
                totalEntries: 0
            };
        }

        return {
            totalPallets: tableData.reduce((sum, item) => sum + (parseInt(item.total_pallet_count) || 0), 0),
            totalEntries: tableData.reduce((sum, item) => sum + (parseInt(item.entry_count) || 0), 0),
        };
    };

    const filteredTotals = calculateFilteredTotals();

    const formatNumber = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        return parseFloat(value).toFixed(0);
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            const aVal = a[column]?.toString().toLowerCase() || '';
            const bVal = b[column]?.toString().toLowerCase() || '';
            return aVal.localeCompare(bVal);
        });
        setTableData(sorted);
    };

    const filterByCustomer = (customer) => {
        if (!customer || customer === 'all') {
            showData();
            setIsFiltered(false);
        } else {
            const filtered = dispatchData.filter(entry => 
                entry.customer_company?.toLowerCase().includes(customer.toLowerCase())
            );
            const formatted = filtered.map((entry) => {
                const newEntry = {};
                displayColumns.forEach(({ key }) => {
                    if (key === 'date') {
                        const dateObj = new Date(entry[key]);
                        newEntry[key] = isNaN(dateObj)
                            ? entry[key]
                            : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    } else if (key === 'order_number') {
                        newEntry[key] = entry[key] ? entry[key] : 'Manual';
                    } else {
                        newEntry[key] = entry[key] ?? '—';
                    }
                });
                return newEntry;
            });
            setTableData(formatted);
            calculateSummaryStats(formatted);
            setIsFiltered(true);
        }
    };

    const handleDownload = () => {
        if (!allowDownload) {
            showAlert({
                message: 'Download not allowed. Please verify your password first.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            return;
        }

        if (!tableData || tableData.length === 0) {
            showAlert({
                message: 'No data available for download.',
                type: 'warning',
                buttons: [{ type: 'warning', label: 'Close' }],
            });
            return;
        }

        const csvData = tableData.map(row => {
            const csvRow = {};
            displayColumns.forEach(({ key, label }) => {
                csvRow[label] = row[key] || '';
            });
            return csvRow;
        });

        downloadReportCSV(csvData, 'Dispatch_Report');
    };

    const handleOrderClick = async (orderData) => {
        if (!orderData.order_number || orderData.order_number === 'Manual') {
            showAlert({
                message: 'No order details available for manual dispatches.',
                type: 'info',
                buttons: [{ type: 'info', label: 'Close' }],
            });
            return;
        }

        setLoadingOrderDetails(true);
        setShowOrderModal(true);

        try {
            const response = await getOrderDetailsByNumber(orderData.order_number);
            
            if (response.success && response.data) {
                setSelectedOrderDetails(response.data.order);
            } else {
                showAlert({
                    message: response.message || 'Failed to fetch order details.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
                setShowOrderModal(false);
            }
        } catch (error) {
            logger.error("Error fetching order details:", error);
            showAlert({
                message: 'An error occurred while fetching order details.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
            setShowOrderModal(false);
        } finally {
            setLoadingOrderDetails(false);
        }
    };

    const handleViewImage = async () => {
        if (!selectedOrderDetails || !selectedOrderDetails.image_path) {
            showAlert({
                message: 'No image available for this order.',
                type: 'info',
                buttons: [{ type: 'info', label: 'Close' }],
            });
            return;
        }

        try {
            const response = await getOrderImageUrl(selectedOrderDetails.id);
            
            if (response.success && response.data.image_url) {
                setOrderImageUrl(response.data.image_url);
                setShowImageModal(true);
            } else {
                showAlert({
                    message: response.message || 'Failed to load image.',
                    type: 'danger',
                    buttons: [{ type: 'danger', label: 'Close' }],
                });
            }
        } catch (error) {
            logger.error("Error loading order image:", error);
            showAlert({
                message: 'An error occurred while loading the image.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    const handleDownloadImage = async () => {
        if (!orderImageUrl || !selectedOrderDetails) return;

        try {
            const response = await fetch(orderImageUrl);
            const blob = await response.blob();
            
            // Create download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Order_${selectedOrderDetails.order_number}_Image.${blob.type.split('/')[1] || 'jpg'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            logger.error("Error downloading image:", error);
            showAlert({
                message: 'Failed to download image.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    const handleDownloadOrderSummary = async () => {
        if (!selectedOrderDetails || selectedOrderDetails.status !== 'dispatched') return;
        setIsDownloadingSummary(true);
        const response = await downloadOrderSummary(selectedOrderDetails.id);
        setIsDownloadingSummary(false);
        if (!response.success) {
            showAlert({
                message: response.message || 'Unable to download order summary.',
                type: 'danger',
                buttons: [{ type: 'danger', label: 'Close' }],
            });
        }
    };

    // Get unique customers for filtering
    const uniqueCustomers = [...new Set(dispatchData.map(entry => entry.customer_company))].sort();

    return (
        <div className="container-fluid p-0">
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            {tableData && tableData.length > 0 && allowDownload ? (
                <div>
                    {/* Summary Stats */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">📤 Dispatch Reports Summary</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row">
                                        <div className="col-md-3">
                                            <strong>Total Dispatches:</strong> {summaryStats.totalOrders}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Unique Customers:</strong> {summaryStats.uniqueCustomers}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Pallets:</strong> {formatNumber(summaryStats.totalPallets)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>System Orders:</strong> {formatNumber(summaryStats.systemOrders)}
                                        </div>
                                    </div>
                                    <div className="row mt-2">
                                        <div className="col-md-3">
                                            <strong>Manual Dispatches:</strong> {formatNumber(summaryStats.manualOrders)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>—</strong>
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Total Log Entries:</strong> {formatNumber(summaryStats.totalEntries)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Report Generated:</strong> {new Date().toLocaleDateString('en-GB')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <div className="row align-items-center">
                                        <div className="col-md-10">
                                            <label className="form-label"><strong>Filter by Customer:</strong></label>
                                            <select
                                                className={`form-select ${isLightTheme ? '' : 'bg-dark text-light border-secondary'}`}
                                                onChange={(e) => filterByCustomer(e.target.value)}
                                                style={{ width: '100%' }}
                                            >
                                                <option value="all">All Customers</option>
                                                {uniqueCustomers.map(customer => (
                                                    <option key={customer} value={customer}>{customer}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Data Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className={`card ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                    <h5 className="mb-0">Dispatch Orders Data ({tableData.length} dispatches)</h5>
                                </div>
                                <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'} p-0`}>
                                    <div className="table-responsive">
                                        <table className={`table table-striped table-hover mb-0 ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                            <thead className={isLightTheme ? 'table-dark' : 'table-light'}>
                                                <tr>
                                                    {displayColumns.map((column) => (
                                                        <th 
                                                            key={column.key} 
                                                            className="text-center"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => sortByColumn(column.key)}
                                                            title={`Click to sort by ${column.label}`}
                                                        >
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {tableData.map((row, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{row.date || 'N/A'}</td>
                                                        <td className="text-center">{row.customer_company || 'N/A'}</td>
                                                        <td className="text-center">
                                                            {row.order_number === 'Manual' ? (
                                                                <span className="badge bg-warning text-dark">Manual</span>
                                                            ) : (
                                                                <span 
                                                                    className="badge bg-primary" 
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => handleOrderClick(row)}
                                                                    title="Click to view order details"
                                                                >
                                                                    {row.order_number || 'N/A'}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-center">{row.delivery_company || 'N/A'}</td>
                                                        <td className="text-center">
                                                            {row.product_names?.length > 50 ? (
                                                                <span title={row.product_names}>
                                                                    {row.product_names.substring(0, 50)}...
                                                                </span>
                                                            ) : (
                                                                row.product_names || 'N/A'
                                                            )}
                                                        </td>
                                                        <td className="text-center">{formatNumber(row.total_pallet_count)}</td>
                                                        <td className="text-center">
                                                            {row.notes?.length > 50 ? (
                                                                <span title={row.notes}>
                                                                    {row.notes.substring(0, 50)}...
                                                                </span>
                                                            ) : (
                                                                row.notes || 'N/A'
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className={isLightTheme ? 'table-secondary' : 'table-light'}>
                                                <tr>
                                                    <td className="text-center"><strong>TOTALS</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                    <td className="text-center"><strong>{formatNumber(filteredTotals.totalPallets)}</strong></td>
                                                    <td className="text-center"><strong>—</strong></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : !allowDownload ? (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>🔐 Access Required</h4>
                            <p>Please enter your password to view the dispatch reports.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    <div className="col-12">
                        <div className={`alert ${isLightTheme ? 'alert-warning' : 'alert-dark'} text-center`}>
                            <h4>📤 No Dispatch Data Available</h4>
                            <p>No dispatch data found for the selected date range. Try selecting a different date range.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Order Details Modal */}
            {showOrderModal && (
                <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-xl">
                        <div className={`modal-content ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                            <div className={`modal-header ${isLightTheme ? 'bg-light text-dark border-dark' : 'bg-dark text-light border-light'} d-flex justify-content-between align-items-center`}>
                                <h5 className="modal-title mb-0">
                                    📋 Order Details
                                    {selectedOrderDetails && ` - ${selectedOrderDetails.order_number}`}
                                </h5>
                                <div className="d-flex align-items-center">
                                    {selectedOrderDetails && selectedOrderDetails.image_path && (
                                        <button 
                                            className="btn btn-sm btn-info me-2"
                                            onClick={handleViewImage}
                                            title="View Order Image"
                                        >
                                            📷 View Image
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setShowOrderModal(false)}
                                        style={{ fontSize: '18px', padding: '2px 8px' }}
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div className={`modal-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                {loadingOrderDetails ? (
                                    <div className="text-center p-4">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading order details...</p>
                                    </div>
                                ) : selectedOrderDetails ? (
                                    <div>
                                        {/* Order Information */}
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <strong>Order Number:</strong> {selectedOrderDetails.order_number || 'N/A'}
                                            </div>
                                            <div className="col-md-6">
                                                <strong>Status:</strong> 
                                                <span className={`badge ms-2 ${
                                                    selectedOrderDetails.status === 'dispatched' ? 'bg-success' :
                                                    selectedOrderDetails.status === 'picked' ? 'bg-warning' :
                                                    selectedOrderDetails.status === 'outstanding' ? 'bg-primary' : 'bg-secondary'
                                                }`}>
                                                    {selectedOrderDetails.status || 'Unknown'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Dispatch Information */}
                                        <div className={`card mb-3 ${isLightTheme ? 'border-dark' : 'border-light'}`}>
                                            <div className={`card-header ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                                <strong>Dispatch Information</strong>
                                            </div>
                                            <div className={`card-body ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                                                <div className="row">
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Dispatched by:</strong> {selectedOrderDetails.dispatched_by_name || 'Unknown'}
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Dispatched at:</strong>{' '}
                                                        {selectedOrderDetails.dispatched_at
                                                            ? new Date(selectedOrderDetails.dispatched_at).toLocaleString('en-GB')
                                                            : 'N/A'}
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Source:</strong>{' '}
                                                        {selectedOrderDetails.dispatch_source === 'device' ? 'Device' : 'Web application'}
                                                    </div>
                                                    <div className="col-md-6 mb-2">
                                                        <strong>Recipient:</strong> {selectedOrderDetails.dispatch_recipient_name || 'N/A'}
                                                    </div>
                                                </div>
                                                {selectedOrderDetails.dispatch_signature_data && (
                                                    <div className="mt-2 text-center">
                                                        <strong className="d-block mb-2">Recipient Signature</strong>
                                                        <img
                                                            src={selectedOrderDetails.dispatch_signature_data}
                                                            alt={`Signature from ${selectedOrderDetails.dispatch_recipient_name || 'recipient'}`}
                                                            style={{
                                                                width: 'min(360px, 100%)',
                                                                maxHeight: '150px',
                                                                objectFit: 'contain',
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #ced4da',
                                                                borderRadius: '4px'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <strong>Customer:</strong> {selectedOrderDetails.customer_name || 'N/A'}
                                            </div>
                                            <div className="col-md-6">
                                                <strong>Created:</strong> {
                                                    selectedOrderDetails.created_at ? 
                                                    new Date(selectedOrderDetails.created_at).toLocaleDateString('en-GB') : 'N/A'
                                                }
                                            </div>
                                        </div>

                                        {/* Delivery Information */}
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <strong>Delivery Company:</strong> {selectedOrderDetails.delivery_company || 'N/A'}
                                            </div>
                                            <div className="col-md-6">
                                                <strong>Delivery Date:</strong> {
                                                    selectedOrderDetails.delivery_date ? 
                                                    new Date(selectedOrderDetails.delivery_date).toLocaleDateString('en-GB') : 'N/A'
                                                }
                                            </div>
                                        </div>

                                        {/* Pallet Handling Information */}
                                        <div className="row mb-3">
                                            <div className="col-md-3">
                                                <strong>Supplied Pallets:</strong> {selectedOrderDetails.supplied_pallets || 0}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Wrapped Pallets:</strong> {selectedOrderDetails.wrapped_pallets || 0}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Banded Pallets:</strong> {selectedOrderDetails.banded_pallets || 0}
                                            </div>
                                            <div className="col-md-3">
                                                <strong>Handled Pallets:</strong> {selectedOrderDetails.handled_pallets || 0}
                                            </div>
                                        </div>

                                        {/* Order Notes */}
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <div className={`alert ${isLightTheme ? 'alert-info' : 'alert-dark'} mb-0`}>
                                                    <strong>Order Notes:</strong> {selectedOrderDetails.notes || 'No notes'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Order Items with Quantity Comparison */}
                                        {selectedOrderDetails.order_items && selectedOrderDetails.order_items.length > 0 && (
                                            <div className="mt-4">
                                                <h6>Order Items - Quantity Analysis:</h6>
                                                <div className="table-responsive">
                                                    <table className={`table table-sm ${isLightTheme ? 'table-light' : 'table-dark'}`}>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '15%' }}>Product Code</th>
                                                                <th style={{ width: '35%' }}>Product Name</th>
                                                                <th style={{ width: '12%' }}>Pallet Code</th>
                                                                <th className="text-end" style={{ width: '13%' }}>Qty Ordered</th>
                                                                <th className="text-end" style={{ width: '13%' }}>Qty Picked</th>
                                                                <th style={{ width: '18%' }}>Reason</th>
                                                                <th className="text-center" style={{ width: '12%' }}>Status</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedOrderDetails.order_items.map((orderItem, idx) => {
                                                                // Find corresponding picked item
                                                                const pickedItem = selectedOrderDetails.picked_items?.find(
                                                                    picked => picked.product_id === orderItem.product_id && 
                                                                             picked.pallet_id === orderItem.pallet_id
                                                                ) || null;
                                                                
                                                                const orderedQty = orderItem.requested_quantity || orderItem.available_quantity || 0;
                                                                const pickedQty = pickedItem?.picked_quantity || 0;
                                                                const difference = pickedQty - orderedQty;
                                                                const reasonKey = `${orderItem.product_id}-${orderItem.pallet_id}`;
                                                                const discrepancyReason = selectedOrderDetails.discrepancy_reasons?.[reasonKey] || '—';
                                                                
                                                                // Determine status
                                                                const getStatus = () => {
                                                                    if (pickedQty === 0) return { text: 'Not Picked', class: 'bg-danger' };
                                                                    if (difference === 0) return { text: 'Complete', class: 'bg-success' };
                                                                    if (difference > 0) return { text: 'Over-picked', class: 'bg-warning text-dark' };
                                                                    return { text: 'Short-picked', class: 'bg-warning text-dark' };
                                                                };
                                                                
                                                                const status = getStatus();
                                                                
                                                                return (
                                                                    <tr key={idx}>
                                                                        <td>{orderItem.product_code || 'N/A'}</td>
                                                                        <td>{orderItem.product_name || 'N/A'}</td>
                                                                        <td>{orderItem.pallet_code || 'N/A'}</td>
                                                                        <td className="text-end">
                                                                            <strong>{orderedQty.toLocaleString()}</strong>
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <strong style={{ color: pickedQty === 0 ? '#dc3545' : pickedQty === orderedQty ? '#198754' : '#fd7e14' }}>
                                                                                {pickedQty.toLocaleString()}
                                                                            </strong>
                                                                        </td>
                                                                        <td>{discrepancyReason}</td>
                                                                        <td className="text-center">
                                                                            <span className={`badge ${status.class}`}>
                                                                                {status.text}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                
                                                {/* Summary Statistics */}
                                                <div className="row mt-3">
                                                    <div className="col-12">
                                                        <div className={`alert ${isLightTheme ? 'alert-info' : 'alert-dark'} mb-0`}>
                                                            <div className="row text-center">
                                                                <div className="col-md-3">
                                                                    <strong>Total Items:</strong><br/>
                                                                    {selectedOrderDetails.order_items.length}
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <strong>Total Ordered:</strong><br/>
                                                                    {selectedOrderDetails.order_items.reduce((sum, item) => 
                                                                        sum + (item.requested_quantity || item.available_quantity || 0), 0
                                                                    ).toLocaleString()}
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <strong>Total Picked:</strong><br/>
                                                                    {(selectedOrderDetails.picked_items || []).reduce((sum, item) => 
                                                                        sum + (item.picked_quantity || 0), 0
                                                                    ).toLocaleString()}
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <strong>Fulfillment Rate:</strong><br/>
                                                                    {(() => {
                                                                        const totalOrdered = selectedOrderDetails.order_items.reduce((sum, item) => 
                                                                            sum + (item.requested_quantity || item.available_quantity || 0), 0
                                                                        );
                                                                        const totalPicked = (selectedOrderDetails.picked_items || []).reduce((sum, item) => 
                                                                            sum + (item.picked_quantity || 0), 0
                                                                        );
                                                                        const rate = totalOrdered > 0 ? (totalPicked / totalOrdered * 100) : 0;
                                                                        return (
                                                                            <span style={{ 
                                                                                color: rate >= 100 ? '#198754' : rate >= 90 ? '#fd7e14' : '#dc3545',
                                                                                fontWeight: 'bold'
                                                                            }}>
                                                                                {rate.toFixed(1)}%
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Delivery Information */}
                                        {selectedOrderDetails.delivery_info && (
                                            <div className="mt-4">
                                                <h6>Delivery Information:</h6>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <strong>Address:</strong><br/>
                                                        {selectedOrderDetails.delivery_info.address || 'N/A'}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <strong>Contact:</strong><br/>
                                                        {selectedOrderDetails.delivery_info.contact || 'N/A'}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center p-4">
                                        <p>No order details available.</p>
                                    </div>
                                )}
                            </div>
                            <div className={`modal-footer ${isLightTheme ? 'bg-light border-dark' : 'bg-dark border-light'}`}>
                                {selectedOrderDetails?.status === 'dispatched'
                                    && (isAdmin || (isCustomer && String(profile?.customer_id) === String(selectedOrderDetails.customer_id))) && (
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={handleDownloadOrderSummary}
                                        disabled={isDownloadingSummary}
                                    >
                                        {isDownloadingSummary ? 'Generating PDF...' : 'Download Order Summary PDF'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowOrderModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && orderImageUrl && (
                <div 
                    className="modal show" 
                    style={{ 
                        display: 'block', 
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1055,
                        overflow: 'hidden'
                    }} 
                    tabIndex="-1"
                >
                    <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxHeight: '100vh', overflow: 'hidden' }}>
                        <div className={`modal-content ${isLightTheme ? 'bg-light text-dark' : 'bg-dark text-light'}`}>
                            <div className={`modal-header ${isLightTheme ? 'bg-light text-dark border-dark' : 'bg-dark text-light border-light'}`}>
                                <h5 className="modal-title">📷 Order Image - {selectedOrderDetails?.order_number}</h5>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary ms-auto"
                                    onClick={() => {
                                        setShowImageModal(false);
                                        setOrderImageUrl(null);
                                    }}
                                    style={{ fontSize: '18px', padding: '2px 8px' }}
                                >
                                    ×
                                </button>
                            </div>
                            <div 
                                className={`modal-body text-center ${isLightTheme ? 'bg-light' : 'bg-dark'}`}
                                style={{ 
                                    padding: '20px',
                                    maxHeight: 'calc(100vh - 200px)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <img 
                                    src={orderImageUrl} 
                                    alt={`Order ${selectedOrderDetails?.order_number}`}
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        border: isLightTheme ? '1px solid #ddd' : '1px solid #555'
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        showAlert({
                                            message: 'Failed to load image.',
                                            type: 'danger',
                                            buttons: [{ type: 'danger', label: 'Close' }],
                                        });
                                        setShowImageModal(false);
                                    }}
                                />
                            </div>
                            <div className={`modal-footer ${isLightTheme ? 'bg-light border-dark' : 'bg-dark border-light'}`}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleDownloadImage}
                                >
                                    💾 Download Image
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowImageModal(false);
                                        setOrderImageUrl(null);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DispatchReport;